import {useEffect, useLayoutEffect, useMemo, useRef, useState, type MouseEvent} from "react";
import {Anchor, Group, Portal, Text, UnstyledButton} from "@mantine/core";
import {PreviewCloseButton} from "./PreviewCloseButton";
import {useTranslation} from "react-i18next";
import IconArrowRight from "../../../assets/arrow-right.svg?react";
import IconBuilding from "../../../assets/building.svg?react";
import {useWorkIsland} from "../work-island/WorkIslandContext";
import {resolveDibHref} from "../../../utils/dibUrls";
import {renderNode} from "../../content/renderNode";
import articleClasses from "../../content/HtmlArticle.module.css";
import {
    PanelContext,
    type PanelContextValue,
} from "../../content/panel/PanelContext";
import classes from "./CitationPreviewBox.module.css";
import {useCitationPreview} from "./useCitationPreview";
import {
    sameTarget,
    targetFromSource,
    useCitationParts,
    type CitationTarget,
} from "./useCitationParts";
import type {CitationPreviewState} from "./CitationPreviewContext";
import {CompanyCard, formatOrgNr} from "./CompanyCard";

const BOX_WIDTH = 460;
const VIEWPORT_MARGIN = 8;
/** The company card is a denser layout than the excerpt panel, with its
 *  own fixed width and viewport edge margin. */
const COMPANY_BOX_WIDTH = 420;
const COMPANY_VIEWPORT_MARGIN = 16;
const ANCHOR_GAP = 6;
/** Fallback for environments where transitionend never fires (jsdom, or a
 *  transition that stops before it finishes). Longer than the slowest transition. */
const CLOSE_FALLBACK_MS = 250;
const OPEN_GRACE_MS = 300;

type Phase = "closed" | "opening" | "open" | "closing";

/**
 * This floating panel shows the current DIB excerpt for a clicked or
 * hovered citation marker. One panel exists per chat view. It moves to
 * whichever marker is active and closes on Escape or a click outside it.
 *
 * DIB parts can contain a collapsed neighbour section, rendered as a link
 * that calls usePanel(). This panel supplies its own local PanelContext
 * instead of the real content-page PanelProvider, so that link retargets
 * the panel in place instead of throwing an error outside a provider.
 *
 * The panel stays mounted through its close transition (phase "closing")
 * so the opacity and transform animation can play, then unmounts.
 */
export const CitationPreviewBox = () => {
    const {t} = useTranslation();
    const {openIsland} = useWorkIsland();
    const {state, closePreview, scheduleClose, cancelScheduledClose} =
        useCitationPreview();
    const boxRef = useRef<HTMLDivElement>(null);
    const [coords, setCoords] = useState<{top: number; left: number} | null>(
        null
    );
    const [flipped, setFlipped] = useState(false);
    const [target, setTarget] = useState<CitationTarget | null>(null);
    const [phase, setPhase] = useState<Phase>("closed");
    const [displayState, setDisplayState] = useState<CitationPreviewState>(null);
    const [showLoading, setShowLoading] = useState(false);

    // Set the open/close phase from the shared preview state. Closing
    // keeps displayState (and this component) mounted until the transition
    // finishes, so the panel stays visible during its close animation.
    useEffect(() => {
        if (state) {
            setDisplayState(state);
            setPhase((current) =>
                current === "closed" || current === "closing" ? "opening" : "open"
            );
        }
    }, [state]);

    // Change the phase from "opening" to "open" on the next frame, so the
    // browser paints the closed values first and the transition to the
    // open values can play.
    useEffect(() => {
        if (phase !== "opening") return;
        const raf = requestAnimationFrame(() => setPhase("open"));
        return () => cancelAnimationFrame(raf);
    }, [phase]);

    // Finish closing once the opacity transition ends, with a fallback
    // timeout for reduced motion or an environment with no transitions.
    useEffect(() => {
        if (phase !== "closing") return;

        let finished = false;
        const finish = () => {
            if (finished) return;
            finished = true;
            setPhase("closed");
            setDisplayState(null);
        };

        const boxEl = boxRef.current;
        const handleTransitionEnd = (event: TransitionEvent) => {
            // Descendant fade transitions must not finish the outer close
            // transition early.
            if (event.target === boxEl && event.propertyName === "opacity") finish();
        };
        boxEl?.addEventListener("transitionend", handleTransitionEnd);
        const timeout = setTimeout(finish, CLOSE_FALLBACK_MS);

        return () => {
            boxEl?.removeEventListener("transitionend", handleTransitionEnd);
            clearTimeout(timeout);
        };
    }, [phase]);

    const companyCard =
        displayState?.kind === "company" ? displayState.company : null;
    const source = displayState?.kind === "citation" ? displayState.source : null;
    const anchorEl = displayState?.anchorEl ?? null;
    const openedBy = displayState?.openedBy;
    const citedTarget = useMemo(() => targetFromSource(source), [source]);

    // A new citation click or hover always retargets the panel to that
    // marker's own source, even when the same marker opens again after the
    // panel had shown a different neighbour section. A company card has no
    // citation target to resolve.
    useEffect(() => {
        setTarget(
            displayState?.kind === "citation"
                ? targetFromSource(displayState.source)
                : null
        );
    }, [displayState]);

    const {status, parts} = useCitationParts(target);

    // Let cached excerpts open immediately. Otherwise wait briefly for the
    // request to finish, then open with plain loading text if it is slow.
    useEffect(() => {
        if (!state) {
            setShowLoading(false);
            setPhase((current) =>
                current === "open" || current === "opening" ? "closing" : current
            );
            return;
        }

        // A company card has no excerpt to fetch, so it opens right away.
        if (
            state.kind === "company" ||
            (status !== "idle" && status !== "loading")
        ) {
            setShowLoading(false);
            setPhase((current) =>
                current === "closed" || current === "closing" ? "opening" : current
            );
            return;
        }

        setShowLoading(false);
        setPhase("closed");
        const timeout = setTimeout(() => {
            setShowLoading(true);
            setPhase((current) =>
                current === "closed" || current === "closing" ? "opening" : current
            );
        }, OPEN_GRACE_MS);
        return () => clearTimeout(timeout);
    }, [state, status]);

    const localPanel = useMemo<PanelContextValue>(
        () => ({
            items: [],
            activeKey: null,
            isOpen: false,
            openPanel: (request) => {
                if (request.fetchType !== "documentParts") {
                    // Related-topic panels are not previewable in this panel.
                    return;
                }
                setTarget({
                    resourceId: request.params.resourceId,
                    segmentId: request.params.segmentId,
                    sectionId: request.params.id,
                });
            },
            closePanel: () => {},
            closeAll: () => {},
            setActivePanel: () => {},
        }),
        []
    );

    // Place the panel next to the marker. Re-run when its content changes size.
    useLayoutEffect(() => {
        if (!anchorEl || !boxRef.current) {
            setCoords(null);
            return;
        }

        const isCompany = displayState?.kind === "company";
        const viewportMargin = isCompany ? COMPANY_VIEWPORT_MARGIN : VIEWPORT_MARGIN;
        const boxWidth = isCompany ? COMPANY_BOX_WIDTH : BOX_WIDTH;

        const place = () => {
            const boxEl = boxRef.current;
            if (!boxEl) return;

            const anchorRect = anchorEl.getBoundingClientRect();
            const boxHeight = boxEl.offsetHeight;
            const width = Math.min(boxWidth, window.innerWidth - viewportMargin * 2);

            let top = anchorRect.bottom + ANCHOR_GAP;
            const overflowsBottom =
                top + boxHeight > window.innerHeight - viewportMargin;
            if (overflowsBottom) {
                // Not enough room below the marker. Show the panel above it instead.
                top = Math.max(
                    viewportMargin,
                    anchorRect.top - boxHeight - ANCHOR_GAP
                );
            }
            setFlipped(overflowsBottom);

            let left = anchorRect.left;
            left = Math.min(left, window.innerWidth - width - viewportMargin);
            left = Math.max(viewportMargin, left);

            setCoords({top, left});
        };

        place();

        const observer = new ResizeObserver(place);
        observer.observe(boxRef.current);
        window.addEventListener("scroll", place, true);
        window.addEventListener("resize", place);

        return () => {
            observer.disconnect();
            window.removeEventListener("scroll", place, true);
            window.removeEventListener("resize", place);
        };
    }, [anchorEl, phase, status, displayState?.kind]);

    // Close on Escape and on a click outside the panel and the marker.
    useEffect(() => {
        if (!anchorEl) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") closePreview();
        };
        const handlePointerDown = (event: PointerEvent) => {
            const clickedNode = event.target as Node;
            if (boxRef.current?.contains(clickedNode)) return;
            if (anchorEl.contains(clickedNode)) return;
            // Leave focus on whatever the user clicked outside the panel.
            closePreview({restoreFocus: false});
        };

        document.addEventListener("keydown", handleKeyDown);
        document.addEventListener("pointerdown", handlePointerDown);
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
            document.removeEventListener("pointerdown", handlePointerDown);
        };
    }, [anchorEl, closePreview]);

    if ((!source && !companyCard) || !anchorEl || phase === "closed") {
        return null;
    }

    const dibHref = source ? resolveDibHref(source.url) : null;
    const width = companyCard
        ? Math.min(COMPANY_BOX_WIDTH, window.innerWidth - COMPANY_VIEWPORT_MARGIN * 2)
        : Math.min(BOX_WIDTH, window.innerWidth * 0.9);
    const showsDrilledTarget = !!source && !sameTarget(target, citedTarget);

    // A DIB section can hold ordinary navigation links (nav-link rule),
    // which do in-app SPA navigation on click. Inside the preview panel, an
    // ordinary link must open a new tab and never navigate the chat away.
    // A "#" href is a panel link (ExmarkLink, PanelLink) and must keep
    // working through the local PanelContext above, not this handler.
    const handleBodyClickCapture = (event: MouseEvent<HTMLDivElement>) => {
        const link = (event.target as HTMLElement).closest("a");
        const href = link?.getAttribute("href");
        if (!link || !href || href === "#") return;

        event.preventDefault();
        event.stopPropagation();
        const resolved = new URL(href, window.location.origin).href;
        window.open(resolved, "_blank", "noopener,noreferrer");
    };

    // A hover-opened panel stays open only while the pointer is on the
    // marker or the panel itself. A click- or keyboard-opened panel only
    // closes on Escape, an outside click, or the close button.
    const handlePointerEnter = () => {
        if (openedBy === "hover") cancelScheduledClose();
    };
    const handlePointerLeave = () => {
        if (openedBy === "hover") scheduleClose();
    };

    const sourceTitle = companyCard
        ? companyCard.name
        : source!.segmentName
          ? `${source!.name} | ${source!.segmentName}`
          : source!.name;
    const sourceDescription = source
        ? source.description || source.type
        : null;
    const companySubtitle = companyCard
        ? [companyCard.companyType, companyCard.municipality]
              .filter(Boolean)
              .join(" · ")
        : null;

    const boxClassName = [
        classes.box,
        companyCard ? classes.company : "",
        flipped ? classes.flipped : "",
        phase === "open" ? classes.isOpen : "",
        phase === "closing" ? classes.closing : "",
    ]
        .filter(Boolean)
        .join(" ");

    return (
        <Portal>
            <div
                ref={boxRef}
                role="dialog"
                aria-label={sourceTitle}
                className={boxClassName}
                style={{
                    position: "fixed",
                    top: coords?.top ?? -9999,
                    left: coords?.left ?? -9999,
                    width,
                    visibility: coords ? "visible" : "hidden",
                }}
                onPointerEnter={handlePointerEnter}
                onPointerLeave={handlePointerLeave}
            >
                {companyCard ? (
                    <div className={classes.companyHeader}>
                        <Group justify="space-between" wrap="nowrap" align="flex-start">
                            <Group wrap="nowrap" gap="sm" align="flex-start">
                                <IconBuilding
                                    aria-hidden="true"
                                    className={classes.companyIcon}
                                />
                                <div className={classes.companyHeading}>
                                    <Text fw={600} className={classes.companyTitle}>
                                        {sourceTitle}
                                    </Text>
                                    <Text className={classes.companyOrgNr}>
                                        {t("company.orgNr")}{" "}
                                        <span className={classes.companyOrgNrValue}>
                                            {formatOrgNr(companyCard.orgNr)}
                                        </span>
                                    </Text>
                                    {companySubtitle && (
                                        <Text className={classes.companySubtitle}>
                                            {companySubtitle}
                                        </Text>
                                    )}
                                </div>
                            </Group>
                            <PreviewCloseButton
                                label={t("company.close")}
                                onClick={() => closePreview()}
                            />
                        </Group>
                        <div className={classes.companyHeaderRule} />
                    </div>
                ) : (
                    <div className={classes.header}>
                        <Group justify="space-between" wrap="nowrap" align="flex-start">
                            <Text fw={600} lineClamp={2} className={classes.title}>
                                {sourceTitle}
                            </Text>
                            <PreviewCloseButton
                                label={t("Action.Close")}
                                onClick={() => closePreview()}
                            />
                        </Group>
                        {sourceDescription && (
                            <Text size="sm" c="var(--dib-ink)" className={classes.description}>
                                {sourceDescription}
                            </Text>
                        )}
                    </div>
                )}

                <div
                    className={companyCard ? classes.companyBody : classes.body}
                    onClickCapture={handleBodyClickCapture}
                >
                    {companyCard ? (
                        <CompanyCard card={companyCard} />
                    ) : (
                        <>
                            <div className={classes.excerptNotice}>
                                <Text size="xs" c="dimmed">{t("citationPreview.excerptLabel")}</Text>
                            </div>
                            {showsDrilledTarget && (
                                <Anchor
                                    component="button"
                                    type="button"
                                    size="xs"
                                    className={classes.backLink}
                                    onClick={() => setTarget(citedTarget)}
                                >
                                    {t("citationPreview.backToCited")}
                                </Anchor>
                            )}
                            {status === "denied" && (
                                <Text size="sm" c="dimmed">
                                    {t("citationPreview.accessDenied")}
                                </Text>
                            )}
                            {status === "unavailable" && (
                                <Text size="sm" c="dimmed">
                                    {t("citationPreview.unavailable")}
                                </Text>
                            )}
                            {showLoading && (status === "idle" || status === "loading") && (
                                <Text size="sm" c="dimmed">
                                    {t("citationPreview.loading")}
                                </Text>
                            )}
                            {status === "loaded" && parts && (
                                <PanelContext.Provider value={localPanel}>
                                    <div className={`${articleClasses.content} ${classes.excerpt}`}>
                                        {parts.map((part, index) => (
                                            <div key={`citation-preview-part-${index}`}>
                                                {renderNode(
                                                    part,
                                                    part.root[0].id,
                                                    {skipAugmentations: true},
                                                    `citation-preview-part-${index}`
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </PanelContext.Provider>
                            )}
                        </>
                    )}
                </div>

                {dibHref && (
                    <div className={classes.footer}>
                        <UnstyledButton
                            className={classes.footerLink}
                            onClick={() => {
                                openIsland({kind: "topic", source, initialMode: "balanced"});
                                closePreview({restoreFocus: false});
                            }}
                        >
                            {t("citationPreview.goToSource")}
                            <IconArrowRight aria-hidden="true" width={14} height={14} />
                        </UnstyledButton>
                    </div>
                )}

            </div>
        </Portal>
    );
};
